import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';

const projectSchema = z.object({
  title: z.string().min(3, 'Le titre doit contenir au moins 3 caractères').max(255, 'Le titre est trop long'),
  code: z
    .string()
    .max(64, 'Le code est trop long')
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, 'Le code doit contenir uniquement des lettres/chiffres et les caractères - ou _')
    .optional()
    .or(z.literal('')),
  adresse: z.string().max(500, 'L\'adresse est trop longue').optional().or(z.literal('')),
  city: z.string().max(255, 'La ville est trop longue').optional().or(z.literal('')),
});

type ProjectFormData = z.infer<typeof projectSchema>;

export function AddProjectPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuthStore();
  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
  });

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data: ProjectFormData) => projectService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setToastMessage('Projet créé avec succès !');
      setToastType('success');
      setShowToast(true);
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    },
    onError: () => {
      setToastMessage('Erreur lors de la création du projet');
      setToastType('error');
      setShowToast(true);
    },
  });

  const onSubmit = (data: ProjectFormData) => {
    createMutation.mutate(data);
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4 md:hidden">
            <button className="text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-white">
              <span className="material-icons-outlined">menu</span>
            </button>
            <span className="font-bold text-lg dark:text-white">GTB Admin</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Admin</span> / Projets / Nouveau
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? (
                <span className="material-icons-outlined">light_mode</span>
              ) : (
                <span className="material-icons-outlined">dark_mode</span>
              )}
            </button>
            <button className="relative p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors">
              <span className="material-icons-outlined">notifications</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="inline-flex items-center text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              >
                <span className="material-icons-outlined text-lg mr-1">arrow_back</span>
                Retour à la liste
              </button>
            </div>

            <div className="bg-white dark:bg-[#16202A] shadow-sm rounded-xl border border-slate-200 dark:border-[#1E293B] overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-[#1E293B]">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Nouveau Projet</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Créez un nouveau bâtiment ou installation technique.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                {createMutation.isError && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 text-red-800 dark:text-red-400">
                      <span className="material-icons-outlined">error</span>
                      <p className="text-sm font-medium">
                        Erreur lors de la création du projet. Veuillez réessayer.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Title Field */}
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Titre du projet <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="title"
                      type="text"
                      {...register('title')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: Siège Social Paris"
                    />
                    {errors.title && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.title.message}</p>
                    )}
                  </div>

                  {/* Code Field */}
                  <div>
                    <label
                      htmlFor="code"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Code projet
                    </label>
                    <input
                      id="code"
                      type="text"
                      {...register('code')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: SMALT_PARIS_01"
                    />
                    {errors.code && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.code.message}</p>
                    )}
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Optionnel. Lettres/chiffres + <code className="font-mono">-</code> ou <code className="font-mono">_</code>.
                    </p>
                  </div>

                  {/* Address Field */}
                  <div>
                    <label
                      htmlFor="adresse"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Adresse
                    </label>
                    <input
                      id="adresse"
                      type="text"
                      {...register('adresse')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: 12 Rue de la Paix"
                    />
                    {errors.adresse && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.adresse.message}</p>
                    )}
                  </div>

                  {/* City Field */}
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                    >
                      Ville
                    </label>
                    <input
                      id="city"
                      type="text"
                      {...register('city')}
                      className="block w-full px-4 py-3 border border-slate-300 dark:border-[#1E293B] rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                      placeholder="Ex: Paris"
                    />
                    {errors.city && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.city.message}</p>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex gap-3">
                      <span className="material-icons-outlined text-blue-600 dark:text-blue-400 flex-shrink-0">
                        info
                      </span>
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                          Informations sur les projets
                        </p>
                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                          Après la création du projet, vous pourrez ajouter des zones, des boxes GTB et gérer les
                          utilisateurs associés.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200 dark:border-[#1E293B]">
                    <button
                      type="button"
                      onClick={() => navigate('/admin/dashboard')}
                      className="px-6 py-2.5 border border-slate-300 dark:border-[#1E293B] rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="inline-flex items-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createMutation.isPending ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Création en cours...
                        </>
                      ) : (
                        <>
                          <span className="material-icons-outlined text-lg mr-2">add</span>
                          Créer le projet
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
